import L from "leaflet";
import type { BlockGrid } from "@/composables/useSlotGrid";
import type { BlockContainerMap, ContainerSlot } from "@/composables/useContainerStream";

export type ColorMode = 'cargo-empty' | 'by-line' | 'size' | 'vessel' | 'direction'

function slotColor(slot: ContainerSlot, mode: ColorMode): string {
  switch (mode) {
    case 'cargo-empty':
      return slot.status === 'F'
        ? 'rgba(230, 81, 0, 0.55)'   // Full  → deep orange
        : 'rgba(66, 133, 244, 0.45)' // Empty → blue
    // TODO: implement remaining modes when API data is available
    default:
      return 'rgba(100, 116, 139, 0.4)' // placeholder neutral gray
  }
}

interface BlockEntry {
  feature: GeoJSON.Feature;
  grid: BlockGrid;
}

/**
 * Canvas overlay that renders a slot grid for each yard block.
 *
 * Coordinate math:
 *   All yard_block polygons are MultiPolygon rectangles where:
 *     P0 → P1  =  long axis  (bay direction, maxBay divisions)
 *     P0 → P3  =  short axis (row direction, maxRow divisions)
 *
 * Rendering order per block:
 *   1. Fill occupied cells (orange = Full, blue = Empty)
 *   2. Grid lines (semi-transparent)
 *   3. Highlighted cell overlay (cyan, on top)
 */
export class SlotGridLayer extends L.Layer {
  private _canvas: HTMLCanvasElement | null = null;
  private _leafletMap: L.Map | null = null;
  private _blocks: BlockEntry[] = [];

  // Top-tier container per (bay, row) cell, keyed by blockId → "bay:row"
  private _topTier = new Map<string, Map<string, ContainerSlot>>();

  private _highlight: { blockId: string; bayIdx: number; rowIdx: number } | null = null;
  private _colorMode: ColorMode = 'cargo-empty';

  // ── Public API ────────────────────────────────────────────────────────────

  setColorMode(mode: ColorMode): void {
    this._colorMode = mode;
    this._redraw();
  }

  setBlocks(features: GeoJSON.Feature[], grids: BlockGrid[]): void {
    const gridMap = new Map(grids.map((g) => [g.blockId, g]));
    this._blocks = features
      .filter((f) => gridMap.has(f.properties?.block_id))
      .map((f) => ({ feature: f, grid: gridMap.get(f.properties!.block_id)! }));
    this._redraw();
  }

  /**
   * Update container occupancy data.
   * Pre-computes top-tier container for each (bay, row) cell per block.
   */
  setContainers(blockMap: BlockContainerMap): void {
    this._topTier = new Map();

    for (const [blockId, slotMap] of blockMap) {
      const topMap = new Map<string, ContainerSlot>();
      for (const slot of slotMap.values()) {
        if (slot.bayIdx == null || slot.rowIdx == null) continue;
        const key = `${slot.bayIdx}:${slot.rowIdx}`;
        const existing = topMap.get(key);
        if (!existing || (slot.tier ?? 0) > (existing.tier ?? 0)) {
          topMap.set(key, slot);
        }
      }
      this._topTier.set(blockId, topMap);
    }

    this._redraw();
  }

  /**
   * Highlight a specific cell (e.g., from search result).
   * Pass null blockId to clear highlight.
   */
  setHighlight(
    blockId: string | null,
    bayIdx: number | null,
    rowIdx: number | null,
  ): void {
    this._highlight =
      blockId && bayIdx != null && rowIdx != null
        ? { blockId, bayIdx, rowIdx }
        : null;
    this._redraw();
  }

  // ── Leaflet lifecycle ─────────────────────────────────────────────────────

  onAdd(map: L.Map): this {
    this._leafletMap = map;

    this._canvas = document.createElement("canvas");
    Object.assign(this._canvas.style, {
      position: "absolute",
      pointerEvents: "none",
      // z-index 400 matches .leaflet-pane so CSS puts this canvas in step-7 painting
      // (positive z-index), same level as rotatePane and norotatePane. DOM insertion
      // order then decides: rotatePane(0) → canvas(1) → norotatePane(2+).
      zIndex: "400",
    });

    // Insert canvas inside mapPane, before norotatePane (leaflet-rotate).
    // DOM order: rotatePane (tiles/overlay) → [canvas] → norotatePane (markers/popups)
    // This ensures popups/markers always paint above the canvas without any z-index tricks.
    const mapPane = map.getPanes().mapPane;
    const norotatePane = map.getPane("norotatePane");
    if (norotatePane) {
      mapPane.insertBefore(this._canvas, norotatePane);
    } else {
      mapPane.appendChild(this._canvas);
    }

    map.on("move zoomend resize rotate", this._redraw, this);
    this._redraw();
    return this;
  }

  onRemove(map: L.Map): this {
    map.off("move zoomend resize rotate", this._redraw, this);
    this._canvas?.remove();
    this._canvas = null;
    this._leafletMap = null;
    return this;
  }

  // ── Rendering ─────────────────────────────────────────────────────────────

  private _redraw(): void {
    if (!this._leafletMap || !this._canvas) return;

    const size = this._leafletMap.getSize();
    this._canvas.width = size.x;
    this._canvas.height = size.y;

    // Canvas lives inside mapPane which has a pan-translate applied to it.
    // Counter that offset so the canvas visually aligns with the container.
    // Drawing still uses latLngToContainerPoint (screen-space coords), unchanged.
    const mapPane = this._leafletMap.getPanes().mapPane;
    const offset = L.DomUtil.getPosition(mapPane as HTMLElement);
    this._canvas.style.left = `${-offset.x}px`;
    this._canvas.style.top  = `${-offset.y}px`;

    const ctx = this._canvas.getContext("2d")!;
    ctx.clearRect(0, 0, size.x, size.y);

    for (const entry of this._blocks) {
      this._drawBlock(ctx, entry);
    }
  }

  private _drawBlock(
    ctx: CanvasRenderingContext2D,
    { feature, grid }: BlockEntry,
  ): void {
    const geom = feature.geometry as GeoJSON.MultiPolygon;
    const ring = geom.coordinates[0]?.[0]; // exterior ring of first polygon
    if (!ring || ring.length < 4) return;

    // Project corners to canvas pixel coordinates.
    // ring = [P0, P1, P2, P3, P0] — P0→P1 is long axis (bay), P0→P3 is short axis (row).
    const map = this._leafletMap!;
    const project = (pos: GeoJSON.Position): L.Point =>
      map.latLngToContainerPoint(L.latLng(pos[1] as number, pos[0] as number));

    const [p0, p1, , p3] = ring; // skip P2
    const P0 = project(p0!);
    const P1 = project(p1!);
    const P3 = project(p3!);

    // Direction vectors (bay = long axis, row = short axis)
    const bvx = P1.x - P0.x,
      bvy = P1.y - P0.y;
    const rvx = P3.x - P0.x,
      rvy = P3.y - P0.y;

    const { maxBay, maxRow } = grid;
    const blockId: string = feature.properties?.block_id ?? "";

    // ── 1. Fill occupied cells ────────────────────────────────────────────

    const topMap = this._topTier.get(blockId);
    if (topMap) {
      for (const slot of topMap.values()) {
        if (slot.bayIdx == null || slot.rowIdx == null) continue;
        const b = slot.bayIdx - 1; // 0-based
        const r = slot.rowIdx - 1; // 0-based
        if (b < 0 || b >= maxBay || r < 0 || r >= maxRow) continue;

        const tb0 = b / maxBay,
          tb1 = (b + 1) / maxBay;
        const tr0 = r / maxRow,
          tr1 = (r + 1) / maxRow;

        ctx.fillStyle = slotColor(slot, this._colorMode);

        ctx.beginPath();
        ctx.moveTo(P0.x + tb0 * bvx + tr0 * rvx, P0.y + tb0 * bvy + tr0 * rvy);
        ctx.lineTo(P0.x + tb1 * bvx + tr0 * rvx, P0.y + tb1 * bvy + tr0 * rvy);
        ctx.lineTo(P0.x + tb1 * bvx + tr1 * rvx, P0.y + tb1 * bvy + tr1 * rvy);
        ctx.lineTo(P0.x + tb0 * bvx + tr1 * rvx, P0.y + tb0 * bvy + tr1 * rvy);
        ctx.closePath();
        ctx.fill();
      }
    }

    // ── 2. Grid lines ─────────────────────────────────────────────────────

    ctx.beginPath();
    ctx.strokeStyle = "rgba(0, 0, 0, 0.3)";
    ctx.lineWidth = 0.5;

    for (let b = 0; b <= maxBay; b++) {
      const t = b / maxBay;
      ctx.moveTo(P0.x + t * bvx, P0.y + t * bvy);
      ctx.lineTo(P0.x + t * bvx + rvx, P0.y + t * bvy + rvy);
    }

    for (let r = 0; r <= maxRow; r++) {
      const t = r / maxRow;
      ctx.moveTo(P0.x + t * rvx, P0.y + t * rvy);
      ctx.lineTo(P0.x + t * rvx + bvx, P0.y + t * rvy + bvy);
    }

    ctx.stroke();

    // ── 3. Highlight overlay ──────────────────────────────────────────────

    const hl = this._highlight;
    if (hl && hl.blockId === blockId) {
      const b = hl.bayIdx - 1;
      const r = hl.rowIdx - 1;
      if (b >= 0 && b < maxBay && r >= 0 && r < maxRow) {
        const tb0 = b / maxBay,
          tb1 = (b + 1) / maxBay;
        const tr0 = r / maxRow,
          tr1 = (r + 1) / maxRow;

        ctx.fillStyle = "rgba(0, 229, 255, 0.65)";
        ctx.beginPath();
        ctx.moveTo(P0.x + tb0 * bvx + tr0 * rvx, P0.y + tb0 * bvy + tr0 * rvy);
        ctx.lineTo(P0.x + tb1 * bvx + tr0 * rvx, P0.y + tb1 * bvy + tr0 * rvy);
        ctx.lineTo(P0.x + tb1 * bvx + tr1 * rvx, P0.y + tb1 * bvy + tr1 * rvy);
        ctx.lineTo(P0.x + tb0 * bvx + tr1 * rvx, P0.y + tb0 * bvy + tr1 * rvy);
        ctx.closePath();
        ctx.fill();

        // Bright stroke border
        ctx.strokeStyle = "rgba(0, 229, 255, 1)";
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }
    }
  }
}
